import React, { useState } from 'react';
import { Button, message, Modal } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { t } from '../../../../../i18n';

/**
 * 大包进度 -「下载数据」弹窗（不复用 central/parcels 的 ParcelsDownloadModal）。
 *
 * 与 ParcelsDownloadModal 的关键区别：description 文案随子 tab 切换（包裹差异 / 大包差异）；
 * 其它（标题、复制文件名、前往下载页面）保持一致体验。
 */
export interface WorkProgressBagDownloadModalProps {
  visible: boolean;
  onClose: () => void;
  fileName: string;
  /** 描述文案（外部按子 tab 计算后传入）；不传时走默认「下载报告正在后台生成」文案 */
  descriptionText?: string;
}

const WorkProgressBagDownloadModal: React.FC<WorkProgressBagDownloadModalProps> = ({
  visible,
  onClose,
  fileName,
  descriptionText,
}) => {
  const navigate = useNavigate();
  const [copyLoading, setCopyLoading] = useState(false);

  const handleCopyFileName = async () => {
    setCopyLoading(true);
    try {
      await navigator.clipboard.writeText(fileName);
      message.success(t('delivery.parcels.message.downloadCopySuccess'));
    } catch (error) {
      message.error(t('delivery.parcels.message.downloadCopyFailed'));
    } finally {
      setCopyLoading(false);
    }
  };

  const handleGoToDownloadPage = () => {
    navigate('/wms/download');
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <div className="w-1 h-6 bg-purple-500 mr-3"></div>
          <span className="text-lg font-medium">{t('delivery.parcels.title.downloadReport')}</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('components.button.close')}
        </Button>,
        <Button key="copy" icon={<CopyOutlined />} loading={copyLoading} onClick={handleCopyFileName}>
          {t('components.button.copyFilename')}
        </Button>,
        <Button key="download" type="primary" onClick={handleGoToDownloadPage}>
          {t('components.button.goToDownloadPage')}
        </Button>,
      ]}
    >
      <div className="py-4">
        <p className="text-gray-800 font-medium mb-4">
          {descriptionText ?? t('delivery.parcels.message.downloadDescription')}
        </p>
        <div className="bg-gray-50 p-3 rounded-lg mb-4">
          <span className="font-medium">{t('delivery.parcels.label.filename')}: </span>
          <code className="break-all">{fileName}</code>
        </div>
      </div>
    </Modal>
  );
};

export default WorkProgressBagDownloadModal;
